// FirstAccessGate — Sprint B (Secao 20): usuario institucional criado
// administrativamente com credencial provisoria (POST /api/account/users)
// e conduzido por este fluxo minimo antes de continuar operando: definir
// senha pessoal (POST /api/account/password-change, usando a senha
// provisoria como "senha atual") e confirmar/atualizar o e-mail
// (PATCH /api/account/profile). Nao ha "enviamos um e-mail" — SMTP nao
// existe (Secao 22) — o fluxo e inteiramente local/autenticado.
//
// SEC-IDENTITY-01: o servidor impoe a mesma pendencia (passwordChangeGate):
// ate a troca, a sessao so alcanca /api/auth/me, a troca de senha e o
// logout, e o socket e recusado. Este gate e a unica tela possivel nesse
// estado; "Sair" encerra a sessao no servidor.
import React, { useState } from 'react';
import { GlassCard } from '../Ui/GlassCard';
import { PasswordInput } from '../Ui/PasswordInput';
import { ShieldCheck } from 'lucide-react';
import { apiClient } from '../../api/Client';
import { useAuth } from '../../api/AuthContext';
import { playProceduralSound } from '../System/SoundManager';

const inputClass = 'w-full rounded-md border border-gray-700 bg-black/60 px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500';
const labelClass = 'text-xs uppercase tracking-wide text-gray-500';
const buttonClass = 'w-full rounded-md bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 px-4 py-2 text-sm uppercase tracking-wide text-white transition';
const secondaryButtonClass = 'w-full rounded-md border border-gray-700 hover:border-gray-500 px-4 py-2 text-xs uppercase tracking-wide text-gray-400 hover:text-gray-200 transition';

export default function FirstAccessGate() {
    const { user, checkUserAuth, logout } = useAuth();
    const [step, setStep] = useState('password');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState(user?.email || '');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handlePasswordSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        if (newPassword.length < 8) {
            setError('A nova senha precisa ter ao menos 8 caracteres.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('A confirmação não coincide com a nova senha.');
            return;
        }
        if (newPassword === currentPassword) {
            setError('A nova senha precisa ser diferente da senha provisória.');
            return;
        }
        setSubmitting(true);
        try {
            await apiClient.post('/api/account/password-change', { currentPassword, newPassword });
            playProceduralSound('success');
            setStep('email');
        } catch (err) {
            setError(err.message || 'Não foi possível trocar a senha.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEmailSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            if (email && email !== user?.email) {
                await apiClient.patch('/api/account/profile', { email });
            }
            playProceduralSound('success');
            // Reconsulta /api/auth/me: mustChangePassword ja deve estar
            // false apos o passo de senha, o que fecha este gate na
            // proxima renderizacao (Layout le user.mustChangePassword).
            await checkUserAuth();
        } catch (err) {
            setError(err.message || 'Não foi possível confirmar o e-mail.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
            <GlassCard size="lg" className="w-full max-w-lg border-t-4 border-cyan-500">
                <div className="p-8 space-y-6">
                    <div className="flex items-center gap-3">
                        <ShieldCheck className="w-6 h-6 text-cyan-400" />
                        <h2 className="text-lg text-white font-light tracking-widest uppercase">Primeiro Acesso</h2>
                    </div>

                    {step === 'password' && (
                        <form onSubmit={handlePasswordSubmit} className="space-y-4">
                            <p className="text-sm text-gray-400">
                                Sua credencial é provisória. Defina uma senha pessoal para continuar.
                                Até a troca, nenhuma operação, sala ou dado fica disponível.
                            </p>
                            <div className="space-y-2">
                                <label className={labelClass}>Senha provisória</label>
                                <PasswordInput
                                    value={currentPassword}
                                    onChange={(event) => setCurrentPassword(event.target.value)}
                                    className={inputClass}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className={labelClass}>Nova senha</label>
                                <PasswordInput
                                    value={newPassword}
                                    onChange={(event) => setNewPassword(event.target.value)}
                                    className={inputClass}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className={labelClass}>Confirmar nova senha</label>
                                <PasswordInput
                                    value={confirmPassword}
                                    onChange={(event) => setConfirmPassword(event.target.value)}
                                    className={inputClass}
                                    required
                                />
                            </div>
                            {error && <p className="text-sm text-red-400">{error}</p>}
                            <button type="submit" disabled={submitting} className={buttonClass}>
                                {submitting ? 'Processando...' : 'Definir nova senha'}
                            </button>
                        </form>
                    )}

                    {step === 'email' && (
                        <form onSubmit={handleEmailSubmit} className="space-y-4">
                            <p className="text-sm text-gray-400">
                                Confirme ou atualize seu e-mail institucional.
                            </p>
                            <div className="space-y-2">
                                <label className={labelClass}>E-mail</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    className={inputClass}
                                    required
                                />
                            </div>
                            {error && <p className="text-sm text-red-400">{error}</p>}
                            <button type="submit" disabled={submitting} className={buttonClass}>
                                {submitting ? 'Processando...' : 'Concluir primeiro acesso'}
                            </button>
                        </form>
                    )}

                    {step === 'password' && (
                        <button type="button" onClick={logout} disabled={submitting} className={secondaryButtonClass}>
                            Sair
                        </button>
                    )}
                </div>
            </GlassCard>
        </div>
    );
}
